import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
    Activity, Zap, AlertTriangle, RefreshCcw, TrendingUp, ShieldAlert,
    History,
} from "lucide-react";
import { Card, Row } from "./_shared";

/**
 * Wizard Step 5 — per-connection analytics + safeguard actions.
 *
 * Sources:
 *   • GET /admin/connections/{id}/analytics?range=24h|7d|30d
 *   • POST /admin/connections/{id}/disable / enable / rollback
 *
 * Displays KPI tiles, the per-endpoint breakdown, recent failure log,
 * and live breaker snapshot scoped to this connection. Emergency
 * disable + rollback live in their own danger-zone card at the bottom
 * since they're irreversible-ish actions that matter on-call.
 */
const RANGES = [
    { value: "1h", label: "Last 1h" },
    { value: "24h", label: "Last 24h" },
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
];

export default function AnalyticsStep({ conn, canPublish, onChanged }) {
    const [range, setRange] = useState("24h");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(null); // 'disable' | 'enable' | 'rollback'
    const [err, setErr] = useState(null);

    const load = async () => {
        setLoading(true); setErr(null);
        try {
            const { data } = await api.get(
                `/admin/connections/${conn.id}/analytics?range=${range}`,
            );
            setData(data);
        } catch (e) {
            setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [conn.id, range]); // eslint-disable-line react-hooks/exhaustive-deps

    const disable = async () => {
        const reason = window.prompt(
            "Emergency disable this connection?\n\n" +
            "All flows using it will fail until you re-enable. Enter a short reason for the audit log:",
        );
        if (reason === null) return;
        setBusy("disable");
        try {
            await api.post(`/admin/connections/${conn.id}/disable`, {
                reason: reason.trim() || "no reason given",
            });
            toast.success("Connection disabled");
            onChanged();
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setBusy(null);
        }
    };

    const enable = async () => {
        if (!window.confirm("Re-enable this connection? Flows will resume calling it.")) return;
        setBusy("enable");
        try {
            await api.post(`/admin/connections/${conn.id}/enable`);
            toast.success("Connection re-enabled");
            onChanged();
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setBusy(null);
        }
    };

    const rollback = async () => {
        if (!window.confirm(
            "Roll back to the previous version of this connection?\n\n" +
            "This creates a new editable draft cloned from the prior published " +
            "version (endpoints copied, secrets NOT). After publishing it you " +
            "can retire the current version.",
        )) return;
        setBusy("rollback");
        try {
            const { data } = await api.post(`/admin/connections/${conn.id}/rollback`);
            toast.success(`Rollback draft created — v${data.version}`);
            // Navigate to the new draft.
            window.location.assign(`/connections/${data.id}?step=general`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
            setBusy(null);
        }
    };

    return (
        <div className="space-y-4" data-testid="wizard-analytics-step">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs text-white/55 max-w-2xl">
                    Live runtime metrics for this connection. Sources: <span className="font-mono">connection_call_metrics</span> + the in-memory breaker snapshot.
                </div>
                <div className="flex items-center gap-2">
                    <select value={range} onChange={(e) => setRange(e.target.value)}
                        data-testid="conn-analytics-range"
                        className="bg-white/5 border border-white/10 rounded-md px-2 py-1.5 text-xs">
                        {RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <button onClick={load} disabled={loading}
                        data-testid="conn-analytics-refresh"
                        className="px-2.5 py-1.5 text-xs border border-white/15 rounded-md hover:bg-white/5 inline-flex items-center gap-1.5">
                        <RefreshCcw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </button>
                </div>
            </div>

            {conn.disabled_at && (
                <div className="border border-rose-300/40 rounded-md p-3 bg-rose-300/[0.04] text-xs flex items-center justify-between gap-3 flex-wrap"
                    data-testid="conn-disabled-banner">
                    <div className="text-rose-200 inline-flex items-center gap-2">
                        <ShieldAlert className="w-4 h-4" />
                        <span>
                            <span className="font-medium">Emergency-disabled</span>
                            {conn.disabled_reason && <span className="text-rose-200/80"> · {conn.disabled_reason}</span>}
                            <span className="text-white/45 ml-2 font-mono text-[10px]">
                                {(conn.disabled_at || "").slice(0, 16).replace("T", " ")}
                            </span>
                        </span>
                    </div>
                    {canPublish && (
                        <button onClick={enable} disabled={busy === "enable"}
                            data-testid="conn-enable-btn"
                            className="px-3 py-1.5 text-xs bg-emerald-300 text-black rounded-md disabled:opacity-40">
                            {busy === "enable" ? "Enabling…" : "Re-enable"}
                        </button>
                    )}
                </div>
            )}

            {err && (
                <div className="text-xs text-rose-200 inline-flex items-center gap-2">
                    <AlertTriangle className="w-3 h-3" /> {err}
                </div>
            )}

            {data && (
                <>
                    <KpiRow data={data} />
                    <Sparkline series={data.timeseries || []} />
                    <div className="grid lg:grid-cols-2 gap-3">
                        <PerEndpoint rows={data.per_endpoint || []} />
                        <Breakers rows={data.breakers || []} />
                    </div>
                    <RecentFailures rows={data.recent_failures || []} />
                </>
            )}

            {/* Danger zone — emergency disable + rollback */}
            {canPublish && (
                <div className="border border-rose-300/30 rounded-md p-4 space-y-3"
                    data-testid="conn-danger-zone">
                    <div className="text-xs text-rose-200 inline-flex items-center gap-1.5">
                        <ShieldAlert className="w-3.5 h-3.5" />
                        <span className="font-medium">Danger zone</span>
                    </div>
                    <div className="flex items-center justify-between gap-3 flex-wrap text-xs">
                        <div className="max-w-2xl">
                            <div className="font-medium">Emergency disable</div>
                            <div className="text-white/55 mt-0.5">
                                Instant kill-switch. All api_call invocations against this connection
                                will fail with a clear error until you re-enable. Use during vendor
                                outages or credential leaks.
                            </div>
                        </div>
                        {!conn.disabled_at && (
                            <button onClick={disable} disabled={busy === "disable"}
                                data-testid="conn-disable-btn"
                                className="px-3 py-1.5 text-xs border border-rose-300/40 text-rose-200 rounded-md hover:bg-rose-300/10 disabled:opacity-40">
                                {busy === "disable" ? "Disabling…" : "Emergency disable"}
                            </button>
                        )}
                    </div>
                    <div className="flex items-center justify-between gap-3 flex-wrap text-xs border-t border-white/5 pt-3">
                        <div className="max-w-2xl">
                            <div className="font-medium">Roll back to previous version</div>
                            <div className="text-white/55 mt-0.5">
                                Creates a new editable draft cloned from the prior published version
                                in this connection family <span className="font-mono">({conn.family_id || conn.id})</span>.
                                Endpoints are copied; secrets must be re-attached.
                            </div>
                        </div>
                        <button onClick={rollback} disabled={busy === "rollback"}
                            data-testid="conn-rollback-btn"
                            className="px-3 py-1.5 text-xs border border-amber-300/40 text-amber-200 rounded-md hover:bg-amber-300/10 disabled:opacity-40 inline-flex items-center gap-1.5">
                            <History className="w-3 h-3" />
                            {busy === "rollback" ? "Rolling back…" : "Rollback"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function Kpi({ label, value, hint, testId }) {
    return (
        <div className="border border-white/10 rounded-md p-3 bg-white/[0.02]"
            data-testid={testId}>
            <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
            <div className="text-2xl font-mono mt-1">{value}</div>
            {hint && <div className="text-[10px] text-white/45 mt-1">{hint}</div>}
        </div>
    );
}

function KpiRow({ data }) {
    const t = data.totals || {};
    const successRate = t.calls ? ((t.successes / t.calls) * 100).toFixed(1) : "—";
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <Kpi label="Calls" value={(t.calls || 0).toLocaleString()} testId="conn-kpi-calls" />
            <Kpi label="Success rate" value={`${successRate}%`}
                hint={`${(t.failures || 0).toLocaleString()} fail`}
                testId="conn-kpi-success-rate" />
            <Kpi label="p50" value={`${data.p50}ms`} testId="conn-kpi-p50" />
            <Kpi label="p95" value={`${data.p95}ms`} testId="conn-kpi-p95" />
            <Kpi label="p99" value={`${data.p99}ms`} testId="conn-kpi-p99" />
        </div>
    );
}

function Sparkline({ series }) {
    if (!series.length) {
        return (
            <div className="border border-white/10 rounded-md p-4 text-center text-xs text-white/40"
                data-testid="conn-sparkline-empty">
                No call activity in this range yet.
            </div>
        );
    }
    const max = Math.max(...series.map((b) => b.calls || 0), 1);
    return (
        <div className="border border-white/10 rounded-md p-3" data-testid="conn-sparkline">
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2 inline-flex items-center gap-1">
                <Activity className="w-3 h-3" /> Calls per bucket
            </div>
            <div className="flex items-end gap-0.5 h-24">
                {series.map((b, i) => {
                    const ok = (b.calls || 0) - (b.failures || 0);
                    const okH = (ok / max) * 100;
                    const failH = ((b.failures || 0) / max) * 100;
                    return (
                        <div key={i} className="flex-1 min-w-[2px] flex flex-col-reverse"
                            title={`${b.ts}: ${b.calls} calls (${b.failures} fail)`}>
                            <div style={{ height: `${okH}%` }} className="bg-emerald-300/60" />
                            <div style={{ height: `${failH}%` }} className="bg-rose-300/80" />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function PerEndpoint({ rows }) {
    return (
        <Card title={<span className="inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Per-endpoint</span>}>
            {rows.length === 0 ? (
                <div className="text-white/40">No endpoint traffic yet.</div>
            ) : (
                rows.map((r) => (
                    <Row key={r.endpoint_id}
                        k={
                            <span>
                                <span className="font-mono">{r.method} {r.relative_path}</span>
                                <span className="text-white/45 ml-1.5">— {r.name}</span>
                            </span>
                        }
                        v={
                            <span className="font-mono inline-flex items-center gap-2">
                                <span>{r.calls.toLocaleString()} calls</span>
                                <span className={r.success_rate < 0.95 ? "text-amber-300" : "text-emerald-300"}>
                                    {(r.success_rate * 100).toFixed(1)}%
                                </span>
                                <span className="text-white/55">{r.duration_avg}ms avg</span>
                            </span>
                        }
                    />
                ))
            )}
        </Card>
    );
}

function Breakers({ rows }) {
    return (
        <Card title={<span className="inline-flex items-center gap-1"><Zap className="w-3 h-3" /> Live breakers</span>}>
            {rows.length === 0 ? (
                <div className="text-white/40">All breakers clean.</div>
            ) : (
                rows.map((r, idx) => (
                    <div key={`${r.endpoint_id}-${idx}`} className="flex justify-between gap-2 text-xs"
                        data-testid={`conn-breaker-${r.endpoint_id}`}>
                        <span className="font-mono text-white/70 truncate">{r.endpoint_id.slice(0, 8)}…</span>
                        <span className="font-mono">
                            {r.failures}/{r.threshold}
                            {r.is_open ? (
                                <span className="text-rose-200 ml-1">· OPEN ({r.cooldown_remaining_s}s)</span>
                            ) : (
                                <span className="text-emerald-200 ml-1">· closed</span>
                            )}
                        </span>
                    </div>
                ))
            )}
        </Card>
    );
}

function RecentFailures({ rows }) {
    return (
        <div className="border border-white/10 rounded-md overflow-hidden"
            data-testid="conn-recent-failures">
            <div className="px-3 py-2 bg-white/[0.04] text-[10px] uppercase tracking-wider text-white/50">
                Recent failures (last 25)
            </div>
            {rows.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-white/40">No failures.</div>
            ) : (
                <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-wider text-white/50">
                        <tr>
                            <th className="text-left px-3 py-1.5">Time</th>
                            <th className="text-left px-3 py-1.5">Status</th>
                            <th className="text-left px-3 py-1.5">Error</th>
                            <th className="text-left px-3 py-1.5">Duration</th>
                            <th className="text-left px-3 py-1.5">Flow run</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={`${r.ts}-${r.flow_run_id || i}`} className="border-t border-white/5">
                                <td className="px-3 py-1.5 font-mono text-[10px] text-white/55">
                                    {(r.ts || "").slice(0, 19).replace("T", " ")}
                                </td>
                                <td className="px-3 py-1.5 font-mono text-rose-200">{r.status_code ?? "—"}</td>
                                <td className="px-3 py-1.5 font-mono text-rose-200">{r.error_type || "(no type)"}</td>
                                <td className="px-3 py-1.5 font-mono text-white/65">{r.duration_ms}ms</td>
                                <td className="px-3 py-1.5 font-mono text-[10px]">
                                    {r.flow_run_id ? (
                                        <Link to={`/flow-runs/${r.flow_run_id}`} className="text-white/65 hover:text-white">
                                            {r.flow_run_id.slice(0, 8)}…
                                        </Link>
                                    ) : (
                                        <span className="text-white/30">—</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
